import { useMemo } from "react";

import { useFeature, modules } from "byzantine";
import App from "byzantine/src/App";
import { useInstitution } from "byzantine/src/dbbl/businessLogic/entities";

import { ACH_PAYMENT_ROUTE } from "../../../components/transfer/ach/ACHPaymentRoutes";
import ZelleLogo from "../../../components/transfer/ZelleLogo";

import type {
  TransferFeatureTypeFlags,
  TransferFeatureMapping,
  MoneyMovementFeaturesData,
} from "./types";

const transferFeatureMapping = (
  flag: TransferFeatureTypeFlags
): TransferFeatureMapping | null => {
  if (flag === "funds") {
    return {
      icon: "transfer-arrows",
      iconSize: "24px",
      title: "Transfers",
      translationTitle: "heading-transfer",
      description: "Transfer funds to or from an internal or external account",
      translationDescription: "description-transfer",
      route: "/funds",
    };
  }
  if (flag === "bill_pay") {
    return {
      icon: "dollar-bill-line",
      iconSize: "24px",
      title: "Bill pay",
      translationTitle: "heading-bill-pay",
      description: "Pay a bill online or set up future payments",
      translationDescription: "description-bill-pay",
      route: "/bill_pay",
    };
  }
  if (flag === "p2p") {
    return {
      icon: "m2m",
      iconSize: "24px",
      title: "Member to member",
      translationTitle: "heading-m-to-m",
      description: "Send money to a bank member using their account number",
      translationDescription: "description-m-to-m",
      route: "/m2m",
    };
  }
  if (flag === "wires") {
    return {
      icon: "wires",
      iconSize: "24px",
      title: "Wire",
      translationTitle: "heading-wire",
      description: "Send funds to an individual or company",
      translationDescription: "description-wire",
      route: "/wires",
    };
  }
  if (flag === "zelle") {
    return {
      title: <ZelleLogo />,
      description: "Send money to recipients at other financial institutions",
      translationDescription: "description-zelle",
      route: "/zelle",
    };
  }
  if (flag === "loan--principal") {
    return {
      icon: "loan",
      iconSize: "24px",
      title: "Loan",
      translationTitle: "heading-loan",
      description: "Make a loan payment or one-time principal payment",
      translationDescription: "description-loan-principal-payment",
      route: "/loans",
    };
  }
  if (flag === "loan") {
    return {
      icon: "loan",
      iconSize: "24px",
      title: "Loan",
      translationTitle: "heading-loan",
      description: "Make a loan payment or one-time principal payment",
      translationDescription: "description-loan-principal-payment",
      route: "/loans",
    };
  }
  if (flag === "loan--card") {
    return {
      icon: "loan",
      iconSize: "24px",
      title: "Loan",
      translationTitle: "heading-loan",
      description: "Make a loan payment by bank account or debit card",
      translationDescription: "description-loan-pay-by-card",
      route: "/loans",
    };
  }
  if (flag === "ach_payments") {
    return {
      icon: "bank",
      iconSize: "24px",
      title: "ACH payment",
      translationTitle: "ach-payment-card-title",
      description: "Send a standard ACH payment to a recipient",
      translationDescription: "ach-payment-card-content",
      route: `/${ACH_PAYMENT_ROUTE}`,
    };
  }
  if (flag === "bulk_payments") {
    return {
      icon: "list",
      iconSize: "24px",
      title: "Bulk payment",
      translationTitle: "make-a-bulk-payment-label",
      description: "Send payments to multiple recipients",
      translationDescription: "make-bulk-payment-card-description",
      route: "/payment_activity/bulk/new",
    };
  }
  if (flag === "ach_file_import") {
    return {
      icon: "upload",
      iconSize: "24px",
      title: "Import file",
      translationTitle: "file-upload-sidebar-label",
      description: "Upload a CSV or NACHA file to send multiple ACH payments",
      translationDescription: "make-bulk-payment-from-file-description",
      route: "/payment_activity/upload",
    };
  }
  if (flag === "domestic_wire_file_import") {
    return {
      icon: "upload",
      iconSize: "24px",
      title: "Import file",
      translationTitle: "file-upload-sidebar-label",
      description: "Upload a CSV file to send multiple domestic wires",
      translationDescription: "wire-file-import-description",
      route: "/payment_activity/upload",
    };
  }
  if (flag === "file_import") {
    return {
      icon: "upload",
      iconSize: "24px",
      title: "Import file",
      translationTitle: "file-upload-sidebar-label",
      description: "Upload a file to send multiple payments",
      translationDescription: "file-import-both-description",
      route: "/payment_activity/upload",
    };
  }
  if (flag === "payment_activity") {
    return {
      sticky: true,
    };
  }

  return null;
};

const getFileImportFlag = (
  achEnabled: boolean,
  wireEnabled: boolean
): "ach_file_import" | "domestic_wire_file_import" | "file_import" => {
  if (achEnabled && wireEnabled) return "file_import";
  if (achEnabled) return "ach_file_import";
  if (wireEnabled) return "domestic_wire_file_import";
  return "ach_file_import";
};

/**
 * Provides a list of all Transfer methods available to a user.
 * Includes the transfer type name, icon, and description.
 */

type UseMoneyMovementMethodsProps = {
  isTilesPage?: boolean;
};

export const useMoneyMovementMethods = ({
  isTilesPage = false,
}: UseMoneyMovementMethodsProps = {}) => {
  const { featureEnabled } = useFeature();
  const institution = useInstitution();
  const transferMenus = institution?.banking_menus?.transfer?.options;
  const { showWireTransferOptionInMenu } =
    modules.wireTemplates.useWirePermissions();

  const moneyMovementFeaturesData: MoneyMovementFeaturesData[] = useMemo(() => {
    if (!transferMenus) {
      return [];
    }

    let fileImportEmitted = false;

    return (
      transferMenus
        .map((option) => {
          if (option.type === "naf") {
            const app = institution?.apps?.find(
              (a) => a.client_id === option.client_id
            );
            if (!app) return null;
            return { type: option.type, nafData: App.deserialize(app) };
          }
          if (option.type === "funds") {
            if (featureEnabled({ or: ["internal_transfers", "ach"] })) {
              return {
                type: option.type,
                data: transferFeatureMapping(option.type),
              };
            }
            return null;
          }
          if (option.type === "loan") {
            if (featureEnabled({ or: ["internal_transfers", "ach"] })) {
              if (featureEnabled({ or: "loan_payment_by_card" })) {
                return {
                  type: option.type,
                  data: transferFeatureMapping("loan--card"),
                };
              }
              if (featureEnabled({ or: "loan_principal_payment" })) {
                return {
                  type: option.type,
                  data: transferFeatureMapping("loan--principal"),
                };
              }
              if (featureEnabled({ or: "enable_loan_payments_web" })) {
                return {
                  type: option.type,
                  data: transferFeatureMapping("loan"),
                };
              }
            }
          } else if (option.type === "wires") {
            if (!showWireTransferOptionInMenu) {
              return null;
            }
            return {
              type: option.type,
              data: transferFeatureMapping(option.type),
            };
            // there are 2 versions of ach_payments card, the OG one and the new PaymentsEntryCard
          } else if (option.type === "ach_payments") {
            // those who have payment_activity flag will have ach_payments flag
            if (featureEnabled({ or: ["payment_activity"] }) && !isTilesPage) {
              // this is the data for the new PaymentsEntryCard
              return {
                // keep this as type "ach_payments" so it corresponds to the VALID_MOM_OPTIONS
                type: "ach_payments",
                // but pass in "payment_activity" here so we can customize the card
                data: transferFeatureMapping("payment_activity"),
              };
            }

            // this is the OG ach payment entry card
            if (featureEnabled({ or: ["ach_payments"] })) {
              return {
                type: option.type,
                data: transferFeatureMapping(option.type),
              };
            }
          } else if (
            option.type === "bulk_payments" ||
            option.type === "ach_file_import" ||
            option.type === "domestic_wire_file_import"
          ) {
            if (isTilesPage && featureEnabled({ or: [option.type] })) {
              if (option.type === "bulk_payments") {
                return {
                  type: option.type,
                  data: transferFeatureMapping(option.type),
                };
              }
              // ach_file_import and domestic_wire_file_import render as unified file_import
              if (!fileImportEmitted) {
                const achEnabled = featureEnabled({
                  and: ["ach_file_import", "bulk_payments"],
                });
                const wireEnabled = featureEnabled({
                  or: ["domestic_wire_file_import"],
                });
                if (!achEnabled && !wireEnabled) return null;
                fileImportEmitted = true;
                const importFlag = getFileImportFlag(achEnabled, wireEnabled);
                return {
                  type: "file_import" as const,
                  data: transferFeatureMapping(importFlag),
                };
              }
            }
          } else if (featureEnabled({ or: [option.type] })) {
            return {
              type: option.type,
              data: transferFeatureMapping(option.type),
            };
          }

          return null;
        })
        // those that have "sticky" prop will be on top of the list
        .sort((a, b) => (b?.data?.sticky ? 1 : 0) - (a?.data?.sticky ? 1 : 0))
        .filter((d) => d !== null) as MoneyMovementFeaturesData[]
    );
  }, [
    featureEnabled,
    institution?.apps,
    isTilesPage,
    showWireTransferOptionInMenu,
    transferMenus,
  ]);

  return moneyMovementFeaturesData;
};

export const useSidebarMoneyMovementMethods = () => {
  const moneyMovementFeaturesData = useMoneyMovementMethods();
  const { featureEnabled } = useFeature();
  const shouldShowSingleACHPaymentFlow =
    modules.achPayments.useShouldShowSingleACHPaymentFlow();

  return useMemo(() => {
    const achPaymentsEnabled = shouldShowSingleACHPaymentFlow;
    const bulkPaymentsEnabled =
      achPaymentsEnabled && featureEnabled("bulk_payments");
    const bulkPaymentsData =
      transferFeatureMapping("bulk_payments") ?? undefined;
    const achPaymentsData = transferFeatureMapping("ach_payments") ?? undefined;
    const sidebarAchPayments = {
      type: "ach_payments" as const,
      data: achPaymentsData,
    };

    const achFileImportEnabled = featureEnabled({
      and: ["ach_file_import", "bulk_payments"],
    });
    const wireFileImportEnabled = featureEnabled({
      or: ["domestic_wire_file_import"],
    });
    const fileImportData =
      achFileImportEnabled || wireFileImportEnabled
        ? transferFeatureMapping(
            getFileImportFlag(achFileImportEnabled, wireFileImportEnabled)
          ) ?? undefined
        : undefined;

    const sidebarFeatures = moneyMovementFeaturesData.filter((feature) => {
      return !["payment_activity", "ach_payments"].includes(feature.type);
    });

    const leadingFeatures = sidebarFeatures.filter((feature) => {
      return ["funds", "wires"].includes(feature.type);
    });
    const trailingFeatures = sidebarFeatures.filter((feature) => {
      return !["funds", "wires"].includes(feature.type);
    });

    const achOptions: MoneyMovementFeaturesData[] = [];

    if (achPaymentsEnabled) {
      achOptions.push(sidebarAchPayments);

      if (bulkPaymentsEnabled) {
        achOptions.push({
          type: "bulk_payments" as const,
          data: bulkPaymentsData,
        });
      }

      if (achFileImportEnabled && !wireFileImportEnabled) {
        achOptions.push({
          type: "file_import" as const,
          data: fileImportData,
        });
      }
    }

    // Top-level file import only when wire is involved
    const fileImportOption: MoneyMovementFeaturesData[] = wireFileImportEnabled
      ? [{ type: "file_import" as const, data: fileImportData }]
      : [];

    return [
      ...leadingFeatures,
      ...achOptions,
      ...fileImportOption,
      ...trailingFeatures,
    ];
  }, [
    featureEnabled,
    moneyMovementFeaturesData,
    shouldShowSingleACHPaymentFlow,
  ]);
};
